import { useState } from 'react';
import {
  Box, FormControl, InputLabel, Select, MenuItem, TextField,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography,
  Chip, OutlinedInput, Checkbox, ListItemText
} from '@mui/material';
import axios from 'axios';

const API = 'https://attend.zventures.in';

const ReportForm = ({ employees, vehicles, setReports, location, isAdmin, locations }) => {
  const [formLocation, setFormLocation]       = useState(isAdmin ? '' : location);
  const [vehicle, setVehicle]                 = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [reportDate, setReportDate]           = useState(new Date().toISOString().split('T')[0]);
  const [openDialog, setOpenDialog]           = useState(false);

  // For HR: location is fixed; for admin: chosen via dropdown
  const activeLocation = isAdmin ? formLocation : location;

  const locationVehicles  = (vehicles  || []).filter(v => v.location === activeLocation);
  const locationEmployees = (employees || []).filter(e => e.location === activeLocation);
  const locationNames     = (locations || []).map(l => l.name);

  const fullName = (emp) => emp.name || '';

  const confirmSendReport = async () => {
    const selectedVehicle = locationVehicles.find(v => v.name === vehicle);
    const report = {
      location:         activeLocation,
      date:             reportDate,
      timestamp:        new Date().toISOString(),
      vehicle:          selectedVehicle,
      presentEmployees: selectedEmployees.map(emp => emp.id)
    };
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/api/create-report`, report, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(prev => [...prev, { ...report, reportId: res.data.reportId }]);
      setOpenDialog(false);
      setSelectedEmployees([]);
      setVehicle('');
      if (isAdmin) setFormLocation('');
    } catch (err) {
      console.error('Error submitting report:', err);
    }
  };

  return (
    <Box sx={{ background: 'linear-gradient(to bottom right,#e6f0f9,#e6f0f9)', borderRadius: '16px', p: 4, mb: '5%' }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 400 }}>
        {isAdmin ? 'Submit Report — Any Location' : `Submit Report — ${location}`}
      </Typography>

      <Box sx={{ backgroundColor: '#ffffffde', borderRadius: '16px', p: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 3 }}>

          {/* Location — admin only */}
          {isAdmin && (
            <FormControl fullWidth>
              <InputLabel>Location</InputLabel>
              <Select value={formLocation} onChange={e => { setFormLocation(e.target.value); setVehicle(''); setSelectedEmployees([]); }} label="Location">
                {locationNames.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
              </Select>
            </FormControl>
          )}

          {/* Vehicle */}
          <FormControl fullWidth disabled={!activeLocation}>
            <InputLabel>Vehicle</InputLabel>
            <Select value={vehicle} onChange={e => setVehicle(e.target.value)} label="Vehicle">
              {locationVehicles.map(v => (
                <MenuItem key={v.name} value={v.name}>
                  {v.name} ({v.capacity} seater, {v.plate})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Employees — multi-select with checkboxes */}
          <FormControl fullWidth disabled={!activeLocation}>
            <InputLabel>Select Employees Present</InputLabel>
            <Select
              multiple
              value={selectedEmployees}
              onChange={e => setSelectedEmployees(e.target.value)}
              input={<OutlinedInput label="Select Employees Present" />}
              renderValue={selected => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map(emp => <Chip key={emp.id} label={fullName(emp)} size="small" />)}
                </Box>
              )}
            >
              {locationEmployees.map(emp => (
                <MenuItem key={emp.id} value={emp}>
                  <Checkbox checked={selectedEmployees.some(s => s.id === emp.id)} />
                  <ListItemText primary={`${fullName(emp)}`} secondary={emp.id} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Date */}
          <TextField
            fullWidth label="Date" type="date"
            value={reportDate} onChange={e => setReportDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            InputProps={{ sx: { backgroundColor: 'white' } }}
          />
        </Box>

        {/* Summary */}
        {selectedEmployees.length > 0 && (
          <Box sx={{ mt: 2, p: 2, background: '#e3f2fd', borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>{selectedEmployees.length}</strong> employee(s) selected as present out of{' '}
              <strong>{locationEmployees.length}</strong> registered in {activeLocation || '—'}
            </Typography>
          </Box>
        )}

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={() => setOpenDialog(true)}
            disabled={!activeLocation || !vehicle || selectedEmployees.length === 0}
            sx={{ backgroundColor: '#0288d1', '&:hover': { backgroundColor: '#0277bd' }, px: 4 }}
          >
            Submit Report
          </Button>
        </Box>
      </Box>

      {/* Confirm Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Confirm Report Submission</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            <strong>Location:</strong> {activeLocation}<br />
            <strong>Date:</strong> {reportDate}<br />
            <strong>Vehicle:</strong> {vehicle}<br />
            <strong>Employees Present:</strong> {selectedEmployees.length} / {locationEmployees.length}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This will send an email report to the configured management contacts for {activeLocation}.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={confirmSendReport} variant="contained" sx={{ backgroundColor: '#0288d1' }}>
            Confirm & Send
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReportForm;